/**
 * Pure helpers for the high-density (canvas) scatter renderer in AdvancedScatter.
 *
 * The component keeps its entire D3 pipeline (scales, axes, colors, targets, fits,
 * selection store). Only the point-drawing layer is swapped for a Canvas 2D layer
 * once the rendered point count exceeds `HIGH_DENSITY_THRESHOLD`. These helpers are
 * the parts that can be unit-tested without a DOM / D3.
 */

/** Above this rendered-point count, AdvancedScatter paints points on a canvas instead of SVG. */
export const HIGH_DENSITY_THRESHOLD = 2000;

/** Max distance (plot-space px) from cursor to a point for tooltip / click hit testing. */
export const CANVAS_HIT_RADIUS_PX = 12;

/** Returns true when the point count warrants the canvas renderer. */
export function shouldUseHighDensityCanvasScatter(count: number): boolean {
    return count > HIGH_DENSITY_THRESHOLD;
}

/** Minimal row shape shared by the SVG and canvas renderers. */
export interface ScatterHitPoint {
    X: number;
    Y: number;
    ID?: number | null;
    [key: string]: unknown;
}

/** A 1D scale (e.g. d3.ScaleLinear) reduced to the call signature these helpers need. */
export type AxisScale = (value: number) => number;

/**
 * Linear scan for the data row whose plotted position is nearest to (plotX, plotY),
 * within `maxRadiusPx`. Coordinates are in plot space (same space as `xScale`/`yScale`
 * output, i.e. d3.pointer(event, plotGroup)). Returns null when nothing is close enough.
 */
export function findNearestScatterPoint<T extends ScatterHitPoint>(
    data: readonly T[],
    plotX: number,
    plotY: number,
    xScale: AxisScale,
    yScale: AxisScale,
    maxRadiusPx: number,
): T | null {
    let best: T | null = null;
    let bestDist = maxRadiusPx * maxRadiusPx;
    for (const d of data) {
        const px = xScale(Number(d.X));
        const py = yScale(Number(d.Y));
        if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
        const dx = px - plotX;
        const dy = py - plotY;
        const dist2 = dx * dx + dy * dy;
        if (dist2 <= bestDist) {
            bestDist = dist2;
            best = d;
        }
    }
    return best;
}

/** Point in viewport/client coordinates, matching the polygon space stored by d3-lasso. */
export interface LassoScreenPoint {
    id: string;
    x: number;
    y: number;
}

/**
 * Subset of SVGGraphicsElement.getScreenCTM() needed to map plot coords to client coords.
 * Declared locally so the helper is testable with a mocked matrix (no real DOM).
 */
export interface ScreenCTMLike {
    a: number;
    b: number;
    c: number;
    d: number;
    e: number;
    f: number;
}

export interface PlotGroupLike {
    getScreenCTM(): ScreenCTMLike | null;
}

/**
 * Maps each data row to viewport/client coordinates for the lasso worker.
 *
 * Critical: d3-lasso stores its polygon as clientX/clientY, so the points fed to the
 * point-in-polygon worker must also be in client space. Using the plot `<g>` screen CTM
 * (instead of `svgRect.left + margin + xScale(...)`) keeps this correct even when the SVG
 * is CSS-scaled. Rows without a usable ID or non-finite position are skipped.
 */
export function buildLassoScreenPoints<T extends ScatterHitPoint>(
    data: readonly T[],
    plotGroup: PlotGroupLike,
    xScale: AxisScale,
    yScale: AxisScale,
): LassoScreenPoint[] {
    const ctm = plotGroup.getScreenCTM();
    if (!ctm) return [];
    const out: LassoScreenPoint[] = [];
    for (const d of data) {
        if (d.ID === undefined || d.ID === null) continue;
        const plotX = xScale(Number(d.X));
        const plotY = yScale(Number(d.Y));
        if (!Number.isFinite(plotX) || !Number.isFinite(plotY)) continue;
        out.push({
            id: String(d.ID),
            x: ctm.a * plotX + ctm.c * plotY + ctm.e,
            y: ctm.b * plotX + ctm.d * plotY + ctm.f,
        });
    }
    return out;
}
